import {
  ApiBackendError,
  ApiError,
  ApiHTTPError,
  ApiNetworkError,
  ApiSystemError,
} from './errors/ApiError';
import {
  AuthExpiredError,
  AuthLoginFailedError,
  AuthLogoutFailedError,
  AuthUnauthenticatedError,
} from './errors/AuthError';
import { TeoError } from './errors/TeoError';
import type { GetDynamicConfigurationSetup, UsageQuota } from './TeoApi.types';
import type { TeoCore } from './TeoCore';
import type { AnalysisConfiguration } from './text/TeoText';

export * from './TeoApi.types';

/** @deprecated use ApiError instead. Legacy alias for slow refactoring */
export type APIError = ApiError;

/** @deprecated use ApiError.matches instead. Legacy alias for slow refactoring */
export const isAPIError = ApiError.matches;

export interface TeoApiConfig {
  apiBaseUrl: string;
  useInsecureHttp?: boolean;
}

/** What a caller can control about a single request. */
export interface RequestOptions {
  /** Aborts the request. The call then fails like any other failed request. */
  signal?: AbortSignal;
}

export class TeoApi {
  apiToken: Maybe<string> = null;
  apiBaseUrl: string;
  apiFullUrl: string;
  useInsecureHttp: boolean;
  private core: TeoCore;
  constructor(core: TeoCore, { apiBaseUrl, useInsecureHttp = false }: TeoApiConfig) {
    this.core = core;
    this.apiBaseUrl = this.normalizeBaseUrl(apiBaseUrl);
    this.useInsecureHttp = useInsecureHttp;
    this.apiFullUrl = `${this.apiBaseUrl}api/v3/`;
  }

  private normalizeBaseUrl(url: string): string {
    return url.endsWith('/') ? url : `${url}/`;
  }

  async setApiToken(apiToken: string | null): Promise<true> {
    if (apiToken === null) {
      this.apiToken = '';
      this.core.user.clearUserDetail();
      return true;
    }
    // TODO: Add request to check if token is still valid
    this.apiToken = apiToken;
    return true;
  }

  async logIn(email: string, password: string) {
    const result = await handleResponse<UserDetail>(() =>
      fetch(`${this.apiFullUrl}login`, {
        method: 'POST',
        body: JSON.stringify({ email, password, insecure: this.useInsecureHttp }),
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    );

    if (!result.ok) {
      return new AuthLoginFailedError(result.error.message, result.error);
    }

    if (!this.setApiToken(result.json.token)) {
      // TODO: how can this happen?
      return new AuthExpiredError();
    }
    return result.json;
  }

  async logOut() {
    // logOut is called from authenticatedGet, so logOut may not call it (because it will be an endless loop)
    try {
      const result = await handleResponse(() =>
        fetch(this.apiFullUrl + 'logout', {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${this.apiToken}`,
          },
        }),
      );
      if (result.ok) {
        return true;
      }
      return new AuthLogoutFailedError(result.error.message, result.error);
    } catch (error) {
      return new AuthLogoutFailedError(error instanceof Error ? error.message : String(error));
    } finally {
      this.setApiToken(null);
    }
  }

  async requestPasswordReset(email: string) {
    const result = await handleResponse<PWResetResult>(() =>
      fetch(`${this.apiFullUrl}resetPassword`, {
        method: 'POST',
        body: JSON.stringify({ email }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }),
    );
    return result.ok ?
        result.json
      : new ApiBackendError(
          { error: 'Password reset request failed', message: result.error.message },
          result.status,
          result.error,
        );
  }

  async getSubscriptionConfig(subscriptionId: string | number) {
    try {
      // fake id for local testing
      const id = subscriptionId === 'localhost' ? 'v3-master.teo.digital' : subscriptionId;

      const endpoint =
        typeof subscriptionId === 'string' ? 'subscriptionConfigByDomain' : 'subscriptionConfig';
      const result = await handleResponse<SubscriptionConfig>(() =>
        fetch(`${this.apiFullUrl}${endpoint}/${id}`, {
          headers: {
            Accept: 'application/json',
          },
        }),
      );
      return result.ok ?
          result.json
        : new ApiBackendError(
            { error: 'Failed to get subscription config', message: result.error.message },
            result.status,
            result.error,
          );
    } catch (error) {
      return new ApiSystemError(error);
    }
  }

  async getDynamicConfigurationSetup(evaluationProfileId: number) {
    const result = await this.authenticatedGet<GetDynamicConfigurationSetup>(
      `dynamicConfigurationSetup/${evaluationProfileId}`,
    );

    return result.ok ?
        result.json
      : new ApiBackendError(
          {
            error: 'Failed to get config options',
            message: result.error.message,
          },
          result.status,
          result.error,
        );
  }

  async getAnalysis(request: AnalysisRequest) {
    request.client_name = 'v3-App';
    const result = await this.authenticatedPost<AnalysisResultData>('analyzeText', request);

    return result.ok ?
        result.json
      : new ApiBackendError(
          { error: 'Analysis failed', message: result.error.message },
          result.status,
          result.error,
        );
  }

  async getAnalysisWithTemplate(request: AnalysisRequestWithTemplateOptions) {
    request.client_name = 'v3-App';
    const result = await this.authenticatedPost<
      AnalysisResultData & { analysisConfig: AnalysisConfiguration }
    >('analyzeTextWithTemplate', request);

    return result.ok ?
        result.json
      : new ApiBackendError(
          { error: 'Analysis failed', message: result.error.message },
          result.status,
          result.error,
        );
  }

  async getWordListScan(request: WordListScanRequest) {
    const endpoint = 'wordLists/scan';
    const result = await this.authenticatedPost(endpoint, request);

    return result.ok ?
        (result.json as WordListScanResult)
      : new ApiBackendError(
          { error: 'Word list scan failed', message: result.error.message },
          result.status,
          result.error,
        );
  }
  async getAiSuggestionV2(request: AiSuggestionV2Request, { signal }: RequestOptions = {}) {
    console.trace('getAiSuggestionV2: ' + request.preset);
    const result = await this.authenticatedPost<AiSuggestionV2Data>(
      'ai/suggestions/selection',
      request,
      { signal },
    );
    console.log('AI suggestion V2 result:', result);
    return result.ok ?
        result.json
      : new ApiBackendError(
          { error: 'AI suggestion failed', message: result.error.message },
          result.status,
          result.error,
        );
  }

  async getAiQuickSuggestions(request: AiQuickSuggestionsRequest, { signal }: RequestOptions = {}) {
    const cleanedRequest: AiQuickSuggestionsRequest = {
      ...request,
      edit_history: request.edit_history?.map((step) => ({
        paragraphs: step.paragraphs,
        tpi: step.tpi,
        instruction_title: step.instruction_title,
        indices: step.indices,
      })),
    };
    const result = await this.authenticatedPost<AiQuickSuggestionsData>(
      'ai/suggestions/quick',
      cleanedRequest,
      { signal },
    );
    return result.ok ?
        result.json
      : new ApiBackendError(
          { error: 'AI quick suggestions failed', message: result.error.message },
          result.status,
          result.error,
        );
  }

  async getAiTextGeneration(request: GeneratorRequest) {
    const endpoint = 'ai/generator';
    const result = await this.authenticatedPost<GeneratorResult>(endpoint, request);

    return result.ok ?
        result.json
      : new ApiBackendError(
          { error: 'AI text generation failed', message: result.error.message },
          result.status,
          result.error,
        );
  }

  /**
   * LanguageTool spell/grammar check.
   *
   * Match `offset`/`length` are UTF-16 code units (Java / JS string indexes) into the
   * annotated string of `text` + `markup` — not Unicode code points, unlike analyzeText.
   * Do not run `adjustMarkerOffsets` on these results.
   */
  async getLanguageToolCheck(request: LanguageToolRequest) {
    const result = await this.authenticatedPost<LanguageToolCheckData>('lt/check', request);
    return result.ok ?
        result.json
      : new ApiBackendError(
          { error: 'LanguageTool check failed', message: result.error.message },
          result.status,
          result.error,
        );
  }

  async getHelpTopic(keyWord: string) {
    const result = await this.authenticatedGet<ApiHelpTopic>(`help/${keyWord}`);
    return result.ok ?
        result.json
      : new ApiBackendError(
          { error: 'Failed to get help topic', message: result.error.message },
          result.status,
          result.error,
        );
  }

  async getGeneratorTemplates(): Promise<ApiError | PromptOptionTemplate[]> {
    const result = await this.authenticatedGet<PromptOptionTemplate[]>('generatorTemplates');
    return result.ok ?
        result.json
      : new ApiBackendError(
          { error: 'Failed to get prompt option templates', message: result.error.message },
          result.status,
          result.error,
        );
  }

  async getEvaluationProfiles(): Promise<ApiError | EvaluationProfile[]> {
    const result = await this.authenticatedGet<EvaluationProfile[]>('evaluationProfiles');
    console.log('Fetched evaluation profiles:', result);
    return result.ok ?
        result.json
      : new ApiBackendError(
          { error: 'Failed to get evaluation profiles', message: result.error.message },
          result.status,
          result.error,
        );
  }

  private async authenticatedGet<TData>(requestName: string) {
    // make sure user is initialized
    await this.core.user.init();

    return this.handleAuthenticatedResponse<TData>(() =>
      fetch(this.apiFullUrl + requestName, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${this.apiToken}`,
        },
      }),
    );
  }

  public async authenticatedPost<TData>(
    requestName: string,
    data: unknown,
    { signal }: RequestOptions = {},
  ) {
    // make sure user is initialized
    await this.core.user.init();
    return this.handleAuthenticatedResponse<TData>(async () =>
      fetch(this.apiFullUrl + requestName, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.apiToken}`,
        },
        signal,
      }),
    );
  }

  private async handleAuthenticatedResponse<TData>(call: () => Promise<Response>) {
    if (!this.apiToken) {
      this.core.user.logOut();
      return errorResult(new AuthUnauthenticatedError(), 403);
    }

    try {
      const response = await call();
      const result = await responseJsonOrError<TData>(response);
      if (!result.ok) {
        if (result.error instanceof AuthUnauthenticatedError) {
          this.core.user.logOut(false);
          return errorResult(result.error, 403);
        }
      }
      return result;
    } catch (error) {
      if (error instanceof TypeError) {
        return errorResult(new ApiNetworkError(error));
      }
      return errorResult(new ApiSystemError(error));
    }
  }
}

// --- error/result handling ---
type OkResult<TData> = { ok: true; json: TData };
type ErrorResult<TError extends TeoError> = { ok: false; error: TError; status: number };

const okResult = <TData>(json: TData): OkResult<TData> => ({ ok: true, json });

function errorResult<TError extends ApiError>(error: TError): ErrorResult<TError>;
function errorResult<TError extends TeoError>(error: TError, status: number): ErrorResult<TError>;
function errorResult<TError extends TeoError>(error: TError, status?: number): ErrorResult<TError> {
  return {
    ok: false,
    error,
    status: ApiError.matches(error) ? (error as unknown as ApiError).status : (status ?? 0),
  };
}

async function handleResponse<TData>(call: () => Promise<Response>) {
  try {
    const response = await call();
    return responseJsonOrError<TData>(response);
  } catch (error) {
    if (error instanceof TypeError) {
      return errorResult(new ApiNetworkError(error));
    }
    return errorResult(new ApiSystemError(error));
  }
}

async function responseJsonOrError<TData>(response: Response) {
  try {
    const json = await response.json();
    const message =
      typeof json === 'object' && json !== null && 'message' in json && json.message ?
        json.message
      : undefined;
    if (response.ok) return okResult(json as TData);
    if (isApiErrorResponse(json) && response.status != 401 && response.status != 403) {
      return errorResult(new ApiBackendError(json, response.status));
    } else {
      const httpError = new ApiHTTPError(response);
      if (httpError.response.status === 403 || httpError.response.status === 401) {
        return errorResult(
          new AuthUnauthenticatedError(message, httpError),
          httpError.response.status,
        );
      }
      return errorResult(httpError);
    }
  } catch (error) {
    return errorResult(new ApiSystemError(error));
  }
}

function isApiErrorResponse(response: unknown): response is { message: string } {
  return typeof response === 'object' && response !== null && 'message' in response;
}

// --- types ---

export interface UserDetail {
  token: string;
  email: string;
  name: string;
  role: UserRole;
  modules: string[];
}

export interface UserRole {
  level: number;
  name: string;
  permissions: string[];
}

export interface AnalysisResultData {
  report_id: string;
  created_at: number;
  tpi: number;
  markers: APITeoMarker[];
  indices: Record<string, SubEvaluation>;
  text_session_id: string;
  instructions: AssistantInstruction[];
  additional_instructions: Instruction[];
  additional_values: AnalysisStatistics & {
    num_whitelisted_words: number;
    num_passive_sentences: number;
    num_dialog_particles: number;
    num_dialog_phrases: number;
    num_causal_connectors: number;
    percent_you: number;
  };
  usage_quota?: UsageQuota<'ANALYSIS'>;
}

export type AnalysisStatisticsEntryId =
  | 'num_words'
  | 'num_long_words'
  | 'num_sentences'
  | 'num_long_sentences'
  | 'num_substantives'
  | 'num_abstracts'
  | 'num_personal_words'
  | 'num_personal_sentences'
  | 'num_impuls_sentences'
  | 'percent_long_words'
  | 'percent_long_sentences'
  | 'percent_substantives'
  | 'percent_abstracts'
  | 'percent_we'
  | 'percent_you'
  | 'word_length'
  | 'sentence_length'
  | 'impuls_weight'
  | 'impuls_weight_per_sentence';

export type AnalysisStatistics = Partial<Record<AnalysisStatisticsEntryId, Maybe<number>>>;

export interface Instruction {
  id: number;
  short_title: string;
  title: string;
  description: string;
  text_property_name: string;
  /** @deprecated We use `SuggestionPreset` instead. */
  ai_prompts: AIPrompt[];
}
export interface AssistantInstruction extends Instruction {
  expected_tpi_change: number;
  expected_tpi_change_minute: number;
  priority_score: number;
  effort_estimate: number;
  doability_factor: number;
  index_name: string;
  ai_preset?: SuggestionPreset;
}

/** @deprecated We use `SuggestionPreset` instead. */
export interface AIPrompt {
  id: number | string;
  title: string;
  description: string;
  type: string;
  available?: boolean;
}

export interface TextProperty {
  title: string;
  num: number;
  marker_names: string[];
}

export interface SubEvaluation {
  title: string;
  score: number;
  text_properties: Record<string, TextProperty>;
  used: boolean;
}

interface CommonAnalysisRequest {
  text: string | TextBlock[];
  use_whitelist?: boolean;
  client_name?: string;
  last_text_property?: string;
  last_instruction_id?: number;
  text_alias?: string;
  text_session_id?: string;
  use_exp_parser?: boolean;
  trigger?: Maybe<'button_click' | 'auto_suggestion'>;
}

export interface AnalysisRequest extends CommonAnalysisRequest {
  audience: number;
  strategy: number;
  medium: number;
  is_informal: number;
}

export interface AnalysisRequestWithTemplateOptions extends CommonAnalysisRequest {
  template_id: string;
  selected_options: Record<string, string | undefined>;
  evaluation_profile_id: number;
}

export interface TextBlock {
  string: string;
  type: 'text' | 'paragraph' | 'linebreak' | 'whitespace';
  length?: number;
  interpret_as?: string;
}

export interface APITeoMarker {
  marker: string;
  /**
   * Start of the marker. Analysis / word-list APIs use Unicode code points;
   * LanguageTool (`groupName: 'spellcheck'`) uses UTF-16 code units (JS string indexes).
   */
  offset: number;
  length: number;
  relation: string | null;
  groupName?: MarkerGroupName;
  color?: MarkerColor;
}

export type MarkerColor = 'error' | 'warning' | 'neutral' | 'correct' | 'accent1' | 'accent2';

export type MarkerGroupName =
  | 'analysis'
  | 'brand-scanner'
  | 'dictionary'
  | 'spellcheck'
  | 'diff'
  | 'white-list';

export interface AdditionalProps {
  replacements?: string[];
  ruleText?: string;
}
export interface ExcerptContext {
  text: string;
  textBefore: string;
  textAfter: string;
  textFocus: string;
  replacementWrapper: string;
}

// incomplete
// export interface AiSuggestionData {
//   suggestions: AiSuggestion[];
//   bad_results: AiSuggestion[];
//   base_result: {
//     tpi: {
//       percentage: number;
//     };
//   };
//   error: boolean;
//   error_message: string;
// }

export interface AiSuggestion {
  text: string;
  teo_result: {
    tpi: {
      percentage: number;
    };
  };
  context: ExcerptContext;
  hidden?: boolean;
}

// incomplete
// export interface SimpleAiSuggestionData {
//   suggestions: string[];
//   error: boolean;
//   error_message: string;
// }

export type SuggestionPreset =
  | 'suggestions_vi'
  | 'suggestions_ai'
  | 'suggestions_hi'
  | 'suggestions_ii_high'
  | 'suggestions_ii_low'
  | 'suggestions_lsentence'
  | 'suggestions_abstract'
  | 'suggestions_noun'
  | 'suggestions_passive'
  | 'suggestions_long_word'
  | 'suggestions_substantiations'
  | 'translate_plain_language'
  | 'rewrite_tpi';

export type PromptEnrichment =
  | 'long_words'
  | 'medium_words'
  | 'long_sentences'
  | 'abstract_nouns'
  | 'simple_nouns';

export interface SuggestionV2Context {
  text: string;
  textBefore?: Maybe<string>;
  textAfter?: Maybe<string>;
  textFocus?: Maybe<string>;
}

interface AiSuggestionV2RequestBase {
  /** Id of the prompt option template (uuid). */
  template_id: string;
  evaluation_profile_id: number;
  /** Ids of the selected prompt snippets (uuids). */
  selected_options: Record<string, string>;
  context: SuggestionV2Context;

  /** Defaults to `true` on the backend. */
  use_whitelist?: boolean;
}
export interface AiSuggestionV2Request extends AiSuggestionV2RequestBase {
  preset?: Maybe<SuggestionPreset>;
  prompt_enrichments?: PromptEnrichment[];
  /**
   * Max 5000 characters. Omit when empty so the preset supplies the instruction.
   * When both this and `preset` are set, the API keeps the rest of the preset and
   * overwrites its instruction with this value.
   */
  instruction?: string;
}

export interface SuggestionV2Result {
  tpi: {
    // value: number;
    percentage: number;
  };
  props: Record<
    'longWords' | 'mediumLongWords' | 'longSentences' | 'abstractNouns' | 'simpleNouns',
    string[]
  >;
  configuration: {
    audience: number;
    strategy: number;
    medium: number;
    profile: number;
    informal: boolean;
  };
}

export interface AiSuggestionV2Data {
  suggestions: {
    text: string;
    result: SuggestionV2Result;
  }[];
  /** Analysis of the unchanged text, for comparison with the suggestions. */
  base_result: SuggestionV2Result;
  /** Omitted when no quota is configured for the subscription. */
  usage_quota?: UsageQuota<'AI-TOOLS'>;
}

export interface SuggestionParagraph {
  /** Opaque, request-scoped id. Echoed back unchanged in the response. */
  id: string;
  text: string;
}

/** A paragraph as it was after one applied auto-optimization step. */
export interface EditHistoryParagraph {
  /** Same request-scoped id as {@link SuggestionParagraph.id} at the time of that step. */
  id: string;
  /** The paragraph text after the step applied. */
  text: string;
}

/** One analysis index as it is sent with an auto-history step. */
export interface HistoryIndexScore {
  title: string;
  score: number;
}

/** One applied rewrite in the current auto-optimization session. */
export interface EditHistoryStep {
  instruction_title?: string;
  paragraphs: EditHistoryParagraph[];
  /** TPI after this step’s analysis. */
  tpi: number;
  /** Used analysis indices after this step, in profile order. */
  indices?: HistoryIndexScore[];
}

interface AiQuickSuggestionsRequestBase {
  /** Id of the prompt option template (uuid). */
  template_id: string;
  evaluation_profile_id: number;
  /** Ids of the selected prompt snippets (uuids). */
  selected_options: Record<string, string>;
  /**
   * All paragraphs of the text, in document order, so that the AI can choose which of them
   * to improve and use the neighbouring paragraphs as context.
   */
  paragraphs: SuggestionParagraph[];
  /** Defaults to `false` on the backend. If `true`, the analysis will not be performed and the response will not contain the analysis results.*/
  omit_analysis?: boolean;
  /** Defaults to `true` on the backend. */
  use_whitelist?: boolean;
  debug?: boolean;
}

export interface AiQuickSuggestionsRequest extends AiQuickSuggestionsRequestBase {
  preset?: Maybe<SuggestionPreset>;
  prompt_enrichments?: PromptEnrichment[];
  /**
   * Max 5000 characters. Omit when empty so the preset supplies the instruction.
   * When both this and `preset` are set, the API keeps the rest of the preset and
   * overwrites its instruction with this value.
   */
  instruction?: string;
  /** Full text at the start of this auto session. Omit with the history while nothing has been applied. */
  original_text?: string;
  /** TPI of {@link original_text}. */
  original_tpi?: number;
  /** Used analysis indices of {@link original_text}, in profile order. */
  original_indices?: HistoryIndexScore[];
  /** Applied rewrites since {@link original_text}, oldest first. */
  edit_history?: EditHistoryStep[];
}

export interface AiQuickSuggestionsData {
  /**
   * May cover only a subset of the requested paragraphs, in any order, and may hold
   * several suggestions for the same paragraph.
   */
  suggestions: {
    /** The `id` of the requested paragraph this suggestion rewrites. */
    id: string;
    text: string;
    /** Analysis of the whole text with this one suggestion applied. Omitted for `omit_analysis`. */
    result?: SuggestionV2Result;
  }[];
  /**
   * Analysis of the unchanged text, for comparison with the suggestions. Omitted for
   * `omit_analysis`.
   */
  base_result?: SuggestionV2Result;
  unable_reason?: string;
  /** Omitted when no quota is configured for the subscription. */
  usage_quota?: UsageQuota<'AI-TOOLS'>;
  debug?: { prompt: string };
}

export type WordListScanRequest = {
  text: TextBlock[];
  ignored_entries: {
    word_list_id: number;
    entry_id: number;
  }[];
  use_exp_parser?: boolean;
  word_list_category?: WordListCategory;
  selected_options: Record<string, string>;
};

export type WordListCategory = 'brand-scanner' | 'dictionary' | 'white-list';

export type WordListScanResult = {
  word_lists: WordListData[];
  markers: APITeoMarker[];
};

export type WordListData = {
  id: number;
  current_text_entries: WordListEntry[];
  description: string;
  name: string;
  order: number;
  relation_type: number;
  title: string;
  use_for_ct: boolean;
  can_use_ai_suggestions?: boolean;
  rules?: WordListRule[];
};

export type WordListEntry = {
  id: number;
  current_relation: number;
  description: string;
  ai_fix_text?: string;
  expressions: WordListExpression[];
  found_by: number;
  found_string: string;
  main_expression_id: number;
  title: string;
  word_list_id: number;
};

export type WordListRule = {
  id: number;
  ai_tag: string;
  description: string;
  title: string;
  word_list_id: number;
};

export type WordListExpression = {
  id: number;
  expression_type: number;
  full_expression: string;
  is_regex: boolean;
  priority: number;
  word_list_id: number;
  word_list_entry_id: number;
};

/**
 * Body for `lt/check`.
 * Response offsets are UTF-16 indexes into the concatenation of each block's `text`,
 * or `markup` for markup blocks (`interpretAs` is what LanguageTool *checks*, not what it indexes).
 */
export type LanguageToolRequest = {
  data: {
    annotation: LanguageToolTextBlock[];
  };
  /** @deprecated overwritten on server side */
  language: string;
};

/**
 * One annotated span. Prefer `markup.length === interpretAs.length` (and matching the
 * editor's character span) so returned offsets line up with the document.
 */
export type LanguageToolTextBlock = {
  text?: string;
  markup?: string;
  interpretAs?: string;
};

export interface LanguageToolCheckData {
  software: {
    name: string;
    version: string;
    buildDate: string;
    apiVersion: number;
    status?: string;
    premium?: boolean;
  };
  language: {
    name: string;
    code: string;
    detectedLanguage: {
      name: string;
      code: string;
    };
  };
  matches: Array<{
    message: string;
    shortMessage?: string;
    /** UTF-16 offset into the annotated string (`text` + `markup`). Same as JS `String` indexes. */
    offset: number;
    /** UTF-16 length of the match. */
    length: number;
    replacements: Array<{
      value: string;
    }>;
    context: {
      text: string;
      offset: number;
      length: number;
    };
    sentence: string;
    rule: {
      id: string;
      subId?: string;
      description: string;
      urls?: Array<{ value: string }>;
      issueType?: string;
      category?: {
        id?: string;
        name?: string;
      };
    };
  }>;
}

export type PWResetResult = {
  success: boolean;
};

export interface SubscriptionConfig {
  generic_users: {
    email: string;
    name: string;
    is_logged_in_api: boolean | 0 | 1;
  }[];
  use_generic_users: boolean | 0 | 1;
  use_named_users: boolean | 0 | 1;
  customer_logo?: string;
  show_brandfit?: boolean | 0 | 1;
  subscription_name?: string;
  usage_quotas?: { ANALYSIS: UsageQuota<'ANALYSIS'>; 'AI-TOOLS': UsageQuota<'AI-TOOLS'> };
}

export type ApiHelpTopicSection = {
  key_word: string;
  title: string;
  text: string;
  sub_topics: {
    key_word: string;
    title: string;
    text: string;
  }[];
};

export type ApiHelpTopic = {
  key_word: string;
  title: string;
  sections: ApiHelpTopicSection[];
};

export type PromptOptionSnippet = {
  id: number;
  title: string;
  text: string;
  sort_category?: string;
};

export type GeneratorTemplateOptions = {
  [category: string]: PromptOptionSnippet[];
};

export type PromptOptionTemplate = {
  id: string;
  title: string;
  options: GeneratorTemplateOptions;
};

export type GeneratorAttachedFileRequest = {
  name: string;
  mime_type: string;
  size: number;
  data_base64: string;
};

export type GeneratorRequest = {
  template_id: string;
  text_content_info: string;
  text_context_info?: string;
  evaluation_profile_id: number;
  selected_options: {
    [category: string]: string;
  };
  attached_files?: GeneratorAttachedFileRequest[];
};

export type GeneratorResult = {
  text: string;
  analysisResult: AnalysisResultData;
  analysisConfig: AnalysisConfiguration;
  error: boolean;
  error_message: string;
};

export type EvaluationProfile = {
  id: number;
  name: string;
};
